import { ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';

interface QueuedAction {
  type: 'create' | 'update' | 'delete';
  entity: string;
  data: any;
  timestamp: number;
}

export const useOfflineManager = () => {
  const isOnline = ref(navigator.onLine);
  const syncQueue = useLocalStorage<QueuedAction[]>('offline_sync_queue', []);
  const hasQueuedActions = ref(false);

  // Update online status
  window.addEventListener('online', () => {
    isOnline.value = true;
    processSyncQueue();
  });
  window.addEventListener('offline', () => {
    isOnline.value = false;
  });

  // Check if we have queued actions
  const updateQueueStatus = () => {
    hasQueuedActions.value = syncQueue.value.length > 0;
  };

  // Add action to queue
  const queueAction = (action: QueuedAction) => {
    syncQueue.value.push({
      ...action,
      timestamp: Date.now(),
    });
    updateQueueStatus();
  };

  // Process sync queue when online
  const processSyncQueue = async () => {
    if (!isOnline.value || syncQueue.value.length === 0) return;

    const processedActions: number[] = [];

    for (let i = 0; i < syncQueue.value.length; i++) {
      const action = syncQueue.value[i];
      try {
        // Implement your sync logic here
        await syncAction(action);
        processedActions.push(i);
      } catch (error) {
        console.error('Failed to sync action:', error);
        // Optionally implement retry logic
      }
    }

    // Remove processed actions
    syncQueue.value = syncQueue.value.filter((_, index) => !processedActions.includes(index));
    updateQueueStatus();
  };

  // Sync a single action
  const syncAction = async (action: QueuedAction) => {
    const baseUrl = '/api';
    const endpoint = `${baseUrl}/${action.entity}`;

    switch (action.type) {
      case 'create':
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action.data),
        });
        break;

      case 'update':
        await fetch(`${endpoint}/${action.data.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action.data),
        });
        break;

      case 'delete':
        await fetch(`${endpoint}/${action.data.id}`, {
          method: 'DELETE',
        });
        break;
    }
  };

  // Clear the sync queue
  const clearSyncQueue = () => {
    syncQueue.value = [];
    updateQueueStatus();
  };

  return {
    isOnline,
    hasQueuedActions,
    queueAction,
    processSyncQueue,
    clearSyncQueue,
  };
};